import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Default Tasklina theme
const defaultTheme = {
  name: 'Tasklina Default',
  colors: {
    primary: '#3B82F6',      // blue-500
    primaryDark: '#2563EB',  // blue-600
    primaryLight: '#60A5FA', // blue-400
    secondary: '#10B981',    // green-500
    accent: '#8B5CF6',       // purple-500
    background: '#FFFFFF',
    backgroundAlt: '#F8FAFC',
    surface: '#FFFFFF',
    text: '#1F2937',         // gray-800
    textSecondary: '#6B7280', // gray-500
    textMuted: '#9CA3AF',    // gray-400
    border: '#E5E7EB',       // gray-200
    borderDark: '#D1D5DB',   // gray-300
    success: '#10B981',      // green-500
    warning: '#F59E0B',      // amber-500
    error: '#EF4444',        // red-500
    info: '#3B82F6',         // blue-500
  },
  fonts: {
    primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    heading: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '3rem',
  },
  borderRadius: {
    sm: '0.125rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  },
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizeHex = (hex) => {
  if (!hex) return null;
  let sanitized = hex.replace('#', '');
  if (sanitized.length === 3) {
    sanitized = sanitized.split('').map((char) => char + char).join('');
  }
  if (sanitized.length !== 6) return null;
  return `#${sanitized}`;
};

const adjustColor = (hex, percent = 0) => {
  const normalized = normalizeHex(hex);
  if (!normalized) return hex;
  const amount = Math.round(2.55 * percent);
  const num = parseInt(normalized.slice(1), 16);
  const r = clamp((num >> 16) + amount, 0, 255);
  const g = clamp(((num >> 8) & 0x00ff) + amount, 0, 255);
  const b = clamp((num & 0x0000ff) + amount, 0, 255);
  return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
};

const buildOrganizationBrandingPayload = (organization, overrides = {}) => {
  if (!organization) return null;

  const brandingSource = {
    ...(organization.branding || {}),
    ...overrides,
  };

  const primary = brandingSource.primaryColor || organization.primaryColor || defaultTheme.colors.primary;
  const secondary = brandingSource.secondaryColor || organization.secondaryColor || defaultTheme.colors.secondary;
  const accent = brandingSource.accentColor || organization.accentColor || defaultTheme.colors.accent;
  const background = brandingSource.backgroundColor || organization.backgroundColor || defaultTheme.colors.backgroundAlt;
  const surface = brandingSource.surfaceColor || organization.surfaceColor || defaultTheme.colors.surface;
  const text = brandingSource.textColor || organization.textColor || defaultTheme.colors.text;
  const textSecondary = brandingSource.textMutedColor || organization.textMutedColor || defaultTheme.colors.textSecondary;
  const borderColor = brandingSource.borderColor || defaultTheme.colors.border;
  const sidebarStyle = brandingSource.sidebarStyle || organization.sidebarStyle || 'gradient';
  const sidebarBase = brandingSource.sidebarColor || organization.sidebarColor || primary;
  const sidebarAccent = brandingSource.sidebarAccentColor || organization.sidebarAccentColor || secondary;
  // Handle light style with appropriate text color
  const isLightSidebar = sidebarStyle === 'light';
  const sidebarText = isLightSidebar 
    ? (brandingSource.sidebarTextColor || organization.sidebarTextColor || '#111827')
    : (brandingSource.sidebarTextColor || organization.sidebarTextColor || '#FFFFFF');
  
  // Generate sidebar background based on style
  let sidebarBackground;
  if (sidebarStyle === 'gradient') {
    sidebarBackground = `linear-gradient(180deg, ${sidebarBase} 0%, ${sidebarAccent} 100%)`;
  } else if (sidebarStyle === 'light') {
    sidebarBackground = '#FFFFFF';
  } else {
    sidebarBackground = sidebarBase; // solid color
  }

  const themeTokens = {
    colors: {
      primary,
      primaryDark: brandingSource.primaryDarkColor || adjustColor(primary, -15),
      primaryLight: brandingSource.primaryLightColor || adjustColor(primary, 18),
      secondary,
      accent,
      background,
      backgroundAlt: brandingSource.backgroundAltColor || adjustColor(background, 4),
      surface,
      text,
      textSecondary,
      border: borderColor,
      sidebar: sidebarBase,
      sidebarAccent,
      sidebarText,
      sidebarMuted: adjustColor(sidebarText, 35),
    },
    fonts: {
      primary: brandingSource.primaryFont || defaultTheme.fonts.primary,
      heading: brandingSource.headingFont || defaultTheme.fonts.heading,
    },
  };

  const brandingMeta = {
    name: organization.name || 'Organization',
    logo: brandingSource.logo || organization.logo || null,
    showBranding: true,
    primaryColor: primary,
    primaryDark: themeTokens.colors.primaryDark,
    primaryLight: themeTokens.colors.primaryLight,
    secondaryColor: secondary,
    accentColor: accent,
    backgroundColor: background,
    surfaceColor: surface,
    textColor: text,
    textMutedColor: textSecondary,
    borderColor,
    sidebarStyle,
    sidebarBackground, // The resolved background (gradient string, hex color, or #FFFFFF)
    sidebarGradient: sidebarStyle === 'gradient' ? sidebarBackground : null,
    sidebarSurface: sidebarStyle === 'solid' ? sidebarBase : (sidebarStyle === 'light' ? '#FFFFFF' : null),
    sidebarTextColor: sidebarText,
    headerStyle: brandingSource.headerStyle || organization.headerStyle || 'themed',
    headerColor: brandingSource.headerColor || organization.headerColor || primary,
    themeName: brandingSource.themeName || organization.themeName || `${organization.name || 'Organization'} Theme`,
    logoPosition: brandingSource.logoPosition || organization.logoPosition || 'left',
    customCSS: brandingSource.customCSS || '',
    subdomain: organization.subdomain,
  };

  return {
    branding: brandingMeta,
    theme: themeTokens,
  };
};

export const ThemeProvider = ({ children }) => {
  const { currentUser, isEnterpriseUser } = useAuth();
  const { currentOrganization } = useOrganization();
  
  const [userTheme, setUserTheme] = useState(defaultTheme);
  const [theme, setTheme] = useState(defaultTheme);
  const [darkMode, setDarkMode] = useState(false);
  const [organizationBranding, setOrganizationBranding] = useState(null);
  const [organizationTheme, setOrganizationTheme] = useState(null);

  // Load theme preferences from localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
    
    const savedTheme = localStorage.getItem('userTheme');
    if (savedTheme) {
      try {
        const parsedTheme = JSON.parse(savedTheme);
        setUserTheme({ ...defaultTheme, ...parsedTheme });
      } catch (error) {
        console.error('Error parsing saved theme:', error);
      }
    }
  }, []);

  // Load organization branding when organization changes
  const mergeThemes = useCallback((baseTheme, overrideTheme) => {
    if (!overrideTheme) return { ...baseTheme };
    return {
      ...baseTheme,
      colors: {
        ...baseTheme.colors,
        ...(overrideTheme.colors || {}),
      },
      fonts: {
        ...baseTheme.fonts,
        ...(overrideTheme.fonts || {}),
      },
    };
  }, []);

  useEffect(() => {
    const activeTheme = mergeThemes(userTheme, organizationTheme);
    setTheme(activeTheme);
  }, [mergeThemes, userTheme, organizationTheme]);

  const removeCustomCSS = () => {
    const existingStyle = document.getElementById('org-custom-styles');
    if (existingStyle) {
      existingStyle.remove();
    }
  };

  const applyOrganizationBranding = useCallback((overrides = {}) => {
    if (!currentOrganization) return;
    const payload = buildOrganizationBrandingPayload(currentOrganization, overrides);
    if (!payload) return;
    setOrganizationBranding(payload.branding);
    setOrganizationTheme(payload.theme);
    if (payload.branding.customCSS) {
      applyCustomCSS(payload.branding.customCSS);
    } else {
      removeCustomCSS();
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (isEnterpriseUser() && currentOrganization) {
      applyOrganizationBranding();
    } else {
      setOrganizationBranding(null);
      setOrganizationTheme(null);
      removeCustomCSS();
    }
  }, [applyOrganizationBranding, currentOrganization, isEnterpriseUser]);

  const applyCustomCSS = (customCSS) => {
    // Remove existing custom styles
    const existingStyle = document.getElementById('org-custom-styles');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Apply new custom styles
    const style = document.createElement('style');
    style.id = 'org-custom-styles';
    style.textContent = customCSS;
    document.head.appendChild(style);
  };

  // Generate CSS custom properties from theme
  const generateCSSVariables = useCallback(() => {
    const cssVariables = {};

    // Colors
    Object.entries(theme.colors).forEach(([key, value]) => {
      cssVariables[`--color-${key}`] = value;
    });

    // Fonts
    Object.entries(theme.fonts).forEach(([key, value]) => {
      cssVariables[`--font-${key}`] = value;
    });

    // Spacing
    Object.entries(theme.spacing).forEach(([key, value]) => {
      cssVariables[`--spacing-${key}`] = value;
    });

    // Border radius
    Object.entries(theme.borderRadius).forEach(([key, value]) => {
      cssVariables[`--radius-${key}`] = value;
    });

    // Shadows
    Object.entries(theme.shadows).forEach(([key, value]) => {
      cssVariables[`--shadow-${key}`] = value;
    });

    // Organization-specific tokens
    if (organizationBranding) {
      cssVariables['--org-primary'] = organizationBranding.primaryColor;
      cssVariables['--org-primary-dark'] = organizationBranding.primaryDark;
      cssVariables['--org-primary-light'] = organizationBranding.primaryLight;
      cssVariables['--org-secondary'] = organizationBranding.secondaryColor;
      cssVariables['--org-accent'] = organizationBranding.accentColor;
      cssVariables['--org-background'] = organizationBranding.backgroundColor;
      cssVariables['--org-surface'] = organizationBranding.surfaceColor;
      cssVariables['--org-text'] = organizationBranding.textColor;
      cssVariables['--org-text-muted'] = organizationBranding.textMutedColor;
      cssVariables['--org-border'] = organizationBranding.borderColor;
      // Use sidebarBackground which handles all styles (gradient, solid, light)
      cssVariables['--org-sidebar-bg'] = organizationBranding.sidebarBackground || organizationBranding.sidebarGradient || organizationBranding.sidebarSurface || '#FFFFFF';
      cssVariables['--org-sidebar-text'] = organizationBranding.sidebarTextColor;
      // Generate muted color based on sidebar text color with opacity
      const sidebarTextColor = organizationBranding.sidebarTextColor || '#FFFFFF';
      const isLightText = sidebarTextColor.toLowerCase() === '#ffffff' || sidebarTextColor.toLowerCase() === '#fff' || sidebarTextColor === 'white';
      cssVariables['--org-sidebar-muted'] = isLightText 
        ? 'rgba(255, 255, 255, 0.7)' 
        : organizationTheme?.colors?.sidebarMuted || 'rgba(0, 0, 0, 0.5)';
      cssVariables['--org-logo-position'] = organizationBranding.logoPosition;
      
      // Header theming - use primary color for header accent
      cssVariables['--org-header-bg'] = organizationBranding.surfaceColor || '#FFFFFF';
      cssVariables['--org-header-text'] = organizationBranding.textColor || '#1F2937';
      cssVariables['--org-header-border'] = organizationBranding.borderColor || '#E5E7EB';
    }
    
    // Dark mode overrides - these will be applied when .dark class is present
    if (darkMode) {
      // Dark mode base colors
      cssVariables['--color-background'] = '#0F172A';
      cssVariables['--color-backgroundAlt'] = '#1E293B';
      cssVariables['--color-surface'] = '#1E293B';
      cssVariables['--color-text'] = '#F1F5F9';
      cssVariables['--color-textSecondary'] = '#94A3B8';
      cssVariables['--color-border'] = '#334155';
      
      // Dark mode organization overrides
      if (organizationBranding) {
        cssVariables['--org-background'] = '#0F172A';
        cssVariables['--org-surface'] = '#1E293B';
        cssVariables['--org-text'] = '#F1F5F9';
        cssVariables['--org-text-muted'] = '#94A3B8';
        cssVariables['--org-border'] = '#334155';
        cssVariables['--org-header-bg'] = '#1E293B';
        cssVariables['--org-header-text'] = '#F1F5F9';
        cssVariables['--org-header-border'] = '#334155';
      }
    }

    return cssVariables;
  }, [organizationBranding, organizationTheme, theme, darkMode]);

  // Apply CSS variables to document root
  useEffect(() => {
    const root = document.documentElement;
    const cssVariables = generateCSSVariables();
    
    Object.entries(cssVariables).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
    
    // Add dark mode class
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme, darkMode, organizationBranding, organizationTheme, generateCSSVariables]);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
  };

  // Listen for AI-triggered theme changes
  useEffect(() => {
    const handleAIThemeChange = (e) => {
      const { mode } = e.detail || {};
      if (mode === 'dark' && !darkMode) {
        setDarkMode(true);
        localStorage.setItem('darkMode', 'true');
      } else if (mode === 'light' && darkMode) {
        setDarkMode(false);
        localStorage.setItem('darkMode', 'false');
      } else if (mode === 'toggle') {
        const newDarkMode = !darkMode;
        setDarkMode(newDarkMode);
        localStorage.setItem('darkMode', newDarkMode.toString());
      }
    };
    window.addEventListener('ai-theme-change', handleAIThemeChange);
    return () => window.removeEventListener('ai-theme-change', handleAIThemeChange);
  }, [darkMode]);

  const updateTheme = (newTheme) => {
    const mergedTheme = {
      ...theme,
      ...newTheme,
      colors: {
        ...theme.colors,
        ...(newTheme.colors || {}),
      },
      fonts: {
        ...theme.fonts,
        ...(newTheme.fonts || {}),
      },
    };
    
    setTheme(mergedTheme);
    localStorage.setItem('userTheme', JSON.stringify(mergedTheme));
  };

  const resetToDefault = () => {
    setTheme(defaultTheme);
    setDarkMode(false);
    localStorage.removeItem('userTheme');
    localStorage.removeItem('darkMode');
    
    // Remove custom CSS
    const existingStyle = document.getElementById('org-custom-styles');
    if (existingStyle) {
      existingStyle.remove();
    }
  };

  // Get organization-specific branding for UI elements
  const getOrganizationBranding = () => {
    if (!isEnterpriseUser() || !currentOrganization) {
      return {
        name: 'Tasklina',
        logo: null,
        showBranding: false,
      };
    }

    return {
      name: currentOrganization.name,
      logo: currentOrganization.logo,
      showBranding: true,
      primaryColor: theme.colors.primary,
      subdomain: currentOrganization.subdomain,
    };
  };

  const value = {
    theme,
    organizationTheme,
    darkMode,
    organizationBranding,
    applyOrganizationBranding,
    toggleDarkMode,
    updateTheme,
    resetToDefault,
    getOrganizationBranding,
    isOrganizationThemed: !!organizationBranding,
    cssVariables: generateCSSVariables(),
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;